import React, { useEffect, useState } from 'react';
import { ArrowUpRightIcon, TrendingUpIcon, TrendingDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface MarketItem {
  name: string;
  subtitle: string;
  price: string;
  changePercent: string;
  changeDollar: string;
  isNegative: boolean;
  sparklinePath: string;
}
const allMarkets: MarketItem[] = [{
  name: 'Choice Cutout',
  subtitle: 'Beef',
  price: '$388.04',
  changePercent: '+0.07%',
  changeDollar: '+$0.26',
  isNegative: false,
  sparklinePath: 'M2,28 L6,26 L10,27 L14,24 L18,25 L22,22 L26,20 L30,18 L34,16 L38,17 L42,14 L46,12 L50,13 L54,10 L58,8 L62,9 L66,7 L70,6 L74,5 L78,4'
}, {
  name: 'Select Cutout',
  subtitle: 'Beef',
  price: '$388.37',
  changePercent: '+0.56%',
  changeDollar: '+$2.18',
  isNegative: false,
  sparklinePath: 'M2,28 L8,26 L14,27 L20,24 L26,22 L32,20 L38,18 L44,19 L50,15 L56,12 L62,10 L68,8 L74,6 L78,4'
}, {
  name: 'Beef Spread',
  subtitle: 'Choice-Select',
  price: '$-0.33',
  changePercent: '-120.75%',
  changeDollar: '-$1.92',
  isNegative: true,
  sparklinePath: 'M2,8 L8,10 L14,9 L20,12 L24,14 L28,16 L30,14 L32,18 L34,16 L36,20 L38,18 L40,22 L44,20 L48,24 L52,22 L56,26 L60,24 L64,26 L68,25 L72,27 L78,28'
}, {
  name: 'Pork Carcass',
  subtitle: 'Pork',
  price: '$99.04',
  changePercent: '+0.09%',
  changeDollar: '+$0.09',
  isNegative: false,
  sparklinePath: 'M2,20 L8,22 L14,18 L20,20 L26,16 L32,18 L38,14 L44,16 L50,12 L56,14 L62,10 L68,8 L74,6 L78,8'
}, {
  name: 'Pork Loin',
  subtitle: 'Pork',
  price: '$91.93',
  changePercent: '-1.21%',
  changeDollar: '-$1.13',
  isNegative: true,
  sparklinePath: 'M2,8 L10,10 L18,12 L26,14 L34,16 L42,18 L50,20 L58,22 L66,24 L74,26 L78,28'
}, {
  name: 'Pork Belly',
  subtitle: 'Pork',
  price: '$154.97',
  changePercent: '+0.10%',
  changeDollar: '+$0.16',
  isNegative: false,
  sparklinePath: 'M2,24 L8,22 L14,20 L20,18 L26,16 L32,14 L38,16 L44,12 L50,10 L56,12 L62,8 L68,6 L74,8 L78,6'
}];
const PAGE_SIZE = 4;
function Sparkline({
  path,
  isNegative
}: {
  path: string;
  isNegative: boolean;
}) {
  const color = isNegative ? '#b91c1c' : '#166534';
  return <svg width="64" height="24" viewBox="0 0 80 32" fill="none" className="flex-shrink-0 opacity-40" data-id="element-225">
      <path d={`${path} L78,32 L2,32 Z`} fill={color} fillOpacity="0.08" data-id="element-226" />
      <path d={path} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-227" />
    </svg>;
}
export function MarketStrip({
  autoScroll = false
}: {
  autoScroll?: boolean;
}) {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(allMarkets.length / PAGE_SIZE);
  useEffect(() => {
    if (!autoScroll) return;
    const interval = setInterval(() => {
      setPage(p => (p + 1) % totalPages);
    }, 4000);
    return () => clearInterval(interval);
  }, [autoScroll, totalPages]);
  const visibleMarkets = allMarkets.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  return <div className="mb-10 border-t border-b border-gray-200 py-5" data-id="element-228">
      {/* Section header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2" data-id="element-229">
        <div className="flex items-baseline gap-3" data-id="element-230">
          <h3 className="text-xs font-bold tracking-widest uppercase text-gray-900" data-id="element-231">
            USDA Market Pulse
          </h3>
          <span className="text-[11px] text-gray-400" data-id="element-232">Updated 04.07.2026</span>
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-4" data-id="element-233">
          <span className="text-[10px] text-gray-400" data-id="element-234">
            Powered by{' '}
            <a href="https://meatmarket.ai/?utm_source=meat_poultry_magazine" target="_blank" rel="noopener noreferrer" className="text-[#002f5f] font-semibold hover:underline" data-id="element-235">
              MeatMarket.ai
            </a>
          </span>
          <a href="/market-iq/beef" className="flex items-center gap-1 text-xs text-[#002f5f] hover:underline" data-id="element-236">
            Launch Market-IQ
            <ArrowUpRightIcon className="w-3 h-3" data-id="element-237" />
          </a>
        </div>
      </div>

      {/* Market cards with arrows */}
      <div className="relative flex items-center" data-id="element-238">
        {/* Left arrow */}
        <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className={`absolute -left-3 z-10 w-7 h-7 rounded-full border border-gray-200 bg-white flex items-center justify-center shadow-sm transition-all ${page === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100 hover:bg-gray-50 hover:border-gray-300'}`} data-id="element-239">
          <ChevronLeftIcon className="w-4 h-4 text-gray-500" data-id="element-240" />
        </button>

        {/* Cards */}
        <div className="flex-1 overflow-hidden" data-id="element-241">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 transition-opacity duration-300 text-sm md:text-base" data-id="element-242">
            {visibleMarkets.map((item, i) => <div key={`${page}-${i}`} className={`flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-gray-50 transition-colors ${i > 0 ? 'border-l border-gray-200' : ''}`} data-id="element-243">
                <a href="/market-iq" className="flex-1 min-w-0 no-underline" data-id="element-244">
                  <div className="flex items-center gap-1.5" data-id="element-245">
                    <span className="text-sm font-semibold text-gray-900 truncate" data-id="element-246">
                      {item.name}
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-400 uppercase tracking-wide" data-id="element-247">
                    {item.subtitle}
                  </div>
                  <div className="flex items-baseline gap-2 mt-1" data-id="element-248">
                    <span className="text-lg font-bold text-gray-900 tabular-nums" data-id="element-249">
                      {item.price}
                    </span>
                    <div className="flex items-center gap-1" data-id="element-250">
                      {item.isNegative ? <TrendingDownIcon className="w-3 h-3 text-red-700" data-id="element-251" /> : <TrendingUpIcon className="w-3 h-3 text-green-800" data-id="element-252" />}
                      <span className={`text-xs font-medium tabular-nums ${item.isNegative ? 'text-red-700' : 'text-green-800'}`} data-id="element-253">
                        {item.changePercent}
                      </span>
                    </div>
                  </div>
                </a>
                <Sparkline path={item.sparklinePath} isNegative={item.isNegative} data-id="element-254" />
              </div>)}
          </div>
        </div>

        {/* Right arrow */}
        <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className={`absolute -right-3 z-10 w-7 h-7 rounded-full border border-gray-200 bg-white flex items-center justify-center shadow-sm transition-all ${page >= totalPages - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100 hover:bg-gray-50 hover:border-gray-300'}`} data-id="element-255">
          <ChevronRightIcon className="w-4 h-4 text-gray-500" data-id="element-256" />
        </button>
      </div>

      {/* Page dots */}
      {totalPages > 1 && <div className="flex justify-center gap-1.5 mt-3" data-id="element-257">
          {Array.from({
        length: totalPages
      }).map((_, i) => <button key={i} onClick={() => setPage(i)} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === page ? 'bg-gray-500' : 'bg-gray-200'}`} data-id="element-258" />)}
        </div>}
    </div>;
}